import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Target, TrendingUp, Shield, Users, PiggyBank, GraduationCap, Home, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import TestimonialsCarousel from '../components/TestimonialsCarousel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ServicesRetail() {
  const [testimonials, setTestimonials] = useState([]);
  const [testimonialsLoaded, setTestimonialsLoaded] = useState(false);

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/testimonials/page/retail`, {
        timeout: 10000 // 10 second timeout
      });
      if (response.data && Array.isArray(response.data)) {
        setTestimonials(response.data);
      } else {
        setTestimonials([]);
      }
    } catch (error) {
      console.error("Error fetching testimonials:", error);
      setTestimonials([]);
    } finally {
      setTestimonialsLoaded(true);
    }
  };

  const services = [
    {
      icon: <Target className="h-12 w-12 text-primary" />,
      title: "Goal-Based SIP Planning",
      description: "Systematic Investment Plans aligned with your specific life goals - education, home, retirement, or wealth creation."
    },
    {
      icon: <Shield className="h-12 w-12 text-secondary" />,
      title: "Portfolio Diversification",
      description: "Spread your investments across asset classes and fund types to optimize risk-adjusted returns."
    },
    {
      icon: <TrendingUp className="h-12 w-12 text-primary" />,
      title: "Wealth Building Strategy",
      description: "Long-term investment strategies focused on compounding and steady growth of your capital."
    },
    {
      icon: <Users className="h-12 w-12 text-secondary" />,
      title: "Family Financial Planning",
      description: "Comprehensive planning for your entire family's financial needs and goals."
    }
  ];

  const goals = [
    { icon: <GraduationCap />, title: "Child's Education", timeline: "10-15 years" },
    { icon: <Home />, title: "Home Purchase", timeline: "5-10 years" },
    { icon: <PiggyBank />, title: "Retirement Planning", timeline: "15+ years" },
    { icon: <Target />, title: "Wealth Creation", timeline: "Long-term" }
  ];

  return (
    <div className="pt-20 min-h-screen" style={{backgroundColor: "#2C3540"}}>
      <section className="relative py-20" style={{backgroundColor: '#2C3540'}}>
        <div className="absolute inset-0 bg-background/90"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="font-heading text-5xl md:text-6xl font-bold text-text-primary mb-6" data-testid="retail-title">
              Services for <span style={{color: "#F5A623"}}>Retail Investors</span>
            </h1>
            <p className="text-xl text-text-secondary">
              Build wealth systematically through goal-based mutual fund investments. Whether you're planning for your child's education, retirement, or wealth creation, we help you achieve your dreams.
            </p>
          </div>
        </div>
      </section>

      <section style={{backgroundColor: "#1F2937"}} className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-4xl font-bold text-center text-text-primary mb-16">Our Services</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <div key={index} className="glass-effect p-8 rounded-2xl hover:-translate-y-1 transition-all" data-testid={`retail-service-${index}`}>
                <div className="mb-4">{service.icon}</div>
                <h3 className="font-heading text-xl font-semibold text-text-primary mb-3">{service.title}</h3>
                <p className="text-text-secondary">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20" style={{backgroundColor: '#2C3540'}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-4xl font-bold text-center text-text-primary mb-16">Common Investment Goals</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {goals.map((goal, index) => (
              <div key={index} className="bg-background p-6 rounded-xl border border-white/10 hover:border-primary/30 transition-all" data-testid={`retail-goal-${index}`}>
                <div className="text-primary mb-4">{React.cloneElement(goal.icon, { className: 'h-10 w-10' })}</div>
                <h3 className="text-lg font-semibold text-text-primary mb-2">{goal.title}</h3>
                <p className="text-text-secondary text-sm">Timeline: {goal.timeline}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <TestimonialsCarousel 
        testimonials={testimonials}
        isLoaded={testimonialsLoaded}
        title="What Our Retail Clients Say"
        subtitle="Stories from individuals and families who've started their investment journey with us"
      />

      <section style={{backgroundColor: "#1F2937"}} className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-4xl font-bold text-text-primary mb-6">Ready to Start Your Investment Journey?</h2>
          <p className="text-xl text-text-secondary mb-8">Let's discuss your goals and create a personalized investment plan.</p>
          <Link to="/contact">
            <Button className="btn-primary text-lg px-10 py-6" data-testid="retail-contact-button">
              Get in Touch <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
